import http from '../../../api/axios';
import { apiUrl } from '../../../api/apiUrl';
import { Values } from '../../../forms/CodeBookItemsPrice/CodeBookItemsPrice.types';
import { handleApiErrors } from '../../../api/apiErrorHandler';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useCurrentCompany } from '../../../hooks/useCompany';
import { paths } from '../../RouterContainer';
import { useTranslation } from '../../../context/translationContext';
import { CodeBookItemsPriceForm } from '../../../forms/CodeBookItemsPrice/CodeBookItemsPriceForm';
import { useEffect, useState } from 'react';
import { CodeBookItemPrice } from '../../../types/CodeBookItemPrice';
import { dateSubmitFormat } from '../../../types/core/date.types';
import { format } from 'date-fns';
import { Loader } from '../../../components/loader/Loader';
import { useBreadcrumbs } from '../../../hooks/useBreadcrumbs';
import { useLoading } from '../../../context/loadingContext';

/**
 * Page component represents code book items overview
 */
export const CodeBookItemsPriceCreatePage = () => {
  const { t } = useTranslation();
  const { itemId } = useParams();
  const navigate = useNavigate();
  const { company } = useCurrentCompany();
  const [data, setData] = useState<CodeBookItemPrice[] | null>(null);
  const [initialValues, setInitialValues] = useState<Values | undefined>();
  const { startLoading, stopLoading } = useLoading();
  const { setBreadcrumbs } = useBreadcrumbs();
  useEffect(() => {
    setBreadcrumbs([
      { path: paths.CODE_BOOK_ITEMS, name: 'navigation.code-book-items' },
      {
        path: `${paths.CODE_BOOK_ITEMS}/edit/${itemId}`,
        name: 'navigation.code-book-item-edit',
      },
      {
        path: `${paths.CODE_BOOK_ITEMS}/${itemId}/${paths.CODE_BOOK_ITEMS_PRICE}/create`,
        name: 'navigation.code-book-item-price-create',
      },
    ]);
  }, []);

  /**
   * Loads company code book items
   */
  const fetchData = async () => {
    if (!company) return;
    const response = await http.apiGet({
      url: `${apiUrl.CODE_BOOK_ITEM_PRICE_LIST}?parentId=${itemId}`,
    });
    setData(response?.data);
  };

  /**
   * Handles create action for new code book item form submit.
   * Uses company create API.
   * @param values form data
   */
  const handleSubmit = async (values: Values) => {
    if (!company) return;
    try {
      startLoading();
      const response = await http.apiPost({
        url: apiUrl.CODE_BOOK_ITEM_PRICE_CREATE,
        data: { ...values, codeBookItemId: itemId },
      });
      if (response?.status === 201) {
        stopLoading();
        toast.success('Success!');
        navigate(`/${paths.CODE_BOOK_ITEMS}/edit/${response.data?.codeBookItemId}`);
      }
    } catch (err) {
      stopLoading();
      handleApiErrors(err);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!data || data.length === 0) return;
    setInitialValues({
      codeBookItemId: itemId ? Number(itemId) : undefined,
      validFrom: data[0].validFrom
        ? format(new Date(data[0].validFrom), dateSubmitFormat)
        : format(new Date(), dateSubmitFormat),
      vatCodeId: data[0].vatCode.id ?? undefined,
      price: data[0].price ?? undefined,
      unit: data[0].unit ?? '',
      currency: data[0].currency ?? '',
    });
  }, [data]);
  if (!data || (data.length > 0 && !initialValues)) return <Loader />;
  return (
    <div className="w-1/4	space-y-8">
      <h1 className="text-3xl font-bold">{t('page.code-book-item-price.headline-create')}</h1>
      <CodeBookItemsPriceForm onSubmit={handleSubmit} initialValues={initialValues} />
    </div>
  );
};
